import { NotFoundError, UnknownErrorScreen } from "@dashboard/library";
import React, { ErrorInfo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { NotFoundErrorScreen } from "@/components/NotFoundErrorScreen/NotFoundErrorScreen";

import * as datadogRumHelpers from "@/helpers/datadogrum/datadogrum.helpers";
import { getEnv } from "@/helpers/environment/environment.helpers";
import { ErrorBoundaryProps, ErrorBoundaryState } from "./ErrorBoundary.decl";

class BaseErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidUpdate(nextProps: ErrorBoundaryProps) {
    const { location } = this.props;
    const { error } = this.state;

    if (location !== nextProps.location && error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (location.pathname === "/debug") {
      console.log("didCatch", {
        error,
        info,
        env: getEnv(),
      });
    }

    if (!(error instanceof NotFoundError)) {
      datadogRumHelpers.addReactError("ErrorBoundary", { error, info });
    }
  }

  render() {
    const { error } = this.state;
    const { children, t } = this.props;

    if (error instanceof NotFoundError) {
      return <NotFoundErrorScreen />;
    }

    if (error) {
      return (
        <UnknownErrorScreen
          title={t("error_screen.generic.title")}
          subtitle={t("error_screen.generic.message")}
          data-test="generic-error-screen"
        />
      );
    }

    return children;
  }
}

export function ErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <BaseErrorBoundary location={location} t={t}>
      {children}
    </BaseErrorBoundary>
  );
}
